import React, { useState } from 'react';
import { PresentationChartBarIcon } from '@heroicons/react/24/outline';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useAgentWithSubagents } from '../../hooks/useAgentWithSubagents';
import { useSubscriptionForRenewalDate } from '../../hooks/useSubscriptionForRenewalDate';
import AgentOutputActions from '../../components/Agents/AgentOutputActions';
import { CreditExhaustedModal } from '../../components/Agents/CreditExhaustedModal';
import { CreditUpgradeNudgeModal } from '../../components/Agents/CreditUpgradeNudgeModal';
import SecurityViolationModal from '../../components/Agents/SecurityViolationModal';
import { validateFields } from '../../utils/securityValidator';
import { formatElapsedTime } from '../../utils/timeUtils';
import { 
  AgentPageLayout, 
  AgentFormSection, 
  AgentFormGroup, 
  AgentLabel, 
  AgentInput, 
  AgentSelect,
  AgentButton, 
  AgentError, 
  AgentResult,
  AgentDisclaimer
} from '../../components/Agents/AgentPageLayout';

const LessonPreparer: React.FC = () => {
  const [biblicalReference, setBiblicalReference] = useState('');
  const [targetAudience, setTargetAudience] = useState('infantil');
  const [skillToAcquire, setSkillToAcquire] = useState('');
  const [mainObjective, setMainObjective] = useState('');
  const [classContext, setClassContext] = useState('');
  const [duration, setDuration] = useState('');
  const [showSecurityModal, setShowSecurityModal] = useState<boolean>(false);
  
  const {
    isLoading,
    error,
    result,
    steps,
    hasStarted,
    elapsedTime,
    execute,
    reset,
    creditsExhausted,
    creditsInfo,
    setCreditsExhausted,
    canRetry,
    onRetry,
    creditUpgradeNudgeOpen,
    creditUpgradeNudgeUsagePercent,
    dismissCreditUpgradeNudge,
  } = useAgentWithSubagents('preparar-aula');
  const renewalDate = useSubscriptionForRenewalDate();

  const ageGroups = [
    { value: 'infantil', label: 'Infantil (0-5 anos)' },
    { value: 'fundamental-i', label: 'Fundamental I (6-10 anos)' },
    { value: 'fundamental-ii', label: 'Fundamental II (11-14 anos)' },
    { value: 'ensino-medio', label: 'Ensino Médio (15-17 anos)' },
    { value: 'jovens-adultos', label: 'Jovens Adultos (18-24 anos)' },
    { value: 'adultos', label: 'Adultos (25+ anos)' },
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!biblicalReference.trim()) {
      return;
    }

    // Validar segurança antes de enviar
    if (validateFields(biblicalReference, mainObjective, skillToAcquire, classContext, duration)) {
      setShowSecurityModal(true);
      return;
    }

    const parameters: Record<string, any> = {
      prompt: `Prepare um plano de aula bíblica didático e envolvente sobre: ${biblicalReference}`,
      passagem: biblicalReference,
      publico_alvo: targetAudience
    };

    if (mainObjective.trim()) {
      parameters.objetivo_principal = mainObjective;
    }
    if (skillToAcquire.trim()) {
      parameters.habilidade_adquirir = skillToAcquire;
    }
    if (classContext.trim()) {
      parameters.contexto_aula = classContext;
    }
    if (duration.trim()) {
      parameters.duracao = duration;
    }

    await execute(parameters);
  };

  const handleReset = () => {
    setBiblicalReference('');
    setTargetAudience('infantil');
    setSkillToAcquire('');
    setMainObjective('');
    setClassContext('');
    setDuration('');
    reset();
  };

  return (
    <AgentPageLayout
      icon={<PresentationChartBarIcon className="w-6 h-6 text-secondary" />}
      title="Preparar Aula"
      subtitle="Prepare planos de aula bíblica didáticos e envolventes para diferentes faixas etárias."
    >
      <AgentFormSection>
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <AgentFormGroup>
            <AgentLabel htmlFor="biblicalReference">Referência bíblica</AgentLabel>
              <AgentInput
                id="biblicalReference"
                type="text"
                value={biblicalReference}
                onChange={(e) => setBiblicalReference(e.target.value)}
                placeholder="Ex: Perdão ou João 3:16"
                disabled={isLoading}
              />
            </AgentFormGroup>

            <AgentFormGroup>
              <AgentLabel htmlFor="targetAudience">Público-Alvo / Faixa Etária</AgentLabel>
              <AgentSelect
                id="targetAudience"
                value={targetAudience}
                onChange={(e) => setTargetAudience(e.target.value)}
                disabled={isLoading}
              >
                {ageGroups.map((group) => (
                  <option key={group.value} value={group.value}>
                    {group.label}
                  </option>
                ))}
              </AgentSelect>
            </AgentFormGroup>

            <AgentFormGroup>
              <AgentLabel htmlFor="skillToAcquire">Habilidade a ser Adquirida</AgentLabel>
              <AgentInput
                id="skillToAcquire"
                type="text"
                value={skillToAcquire}
                onChange={(e) => setSkillToAcquire(e.target.value)}
                placeholder="Ex: Saber perdoar"
                disabled={isLoading}
              />
            </AgentFormGroup>

            <AgentFormGroup>
              <AgentLabel htmlFor="mainObjective">Objetivo Principal</AgentLabel>
              <AgentInput
                id="mainObjective"
                type="text"
                value={mainObjective}
                onChange={(e) => setMainObjective(e.target.value)}
                placeholder="Ex: Aprender a perdoar"
                disabled={isLoading}
              />
            </AgentFormGroup>

            <AgentFormGroup>
              <AgentLabel htmlFor="classContext">Contexto da Aula</AgentLabel>
              <AgentInput
                id="classContext"
                type="text"
                value={classContext}
                onChange={(e) => setClassContext(e.target.value)}
                placeholder="Ex: Conflitos, ofensa, desilusão"
                disabled={isLoading}
              />
            </AgentFormGroup>

            <AgentFormGroup>
              <AgentLabel htmlFor="duration">Duração</AgentLabel>
              <AgentInput
                id="duration"
                type="text"
                value={duration}
                onChange={(e) => setDuration(e.target.value)}
                placeholder="Ex: 1 hora"
                disabled={isLoading}
              />
            </AgentFormGroup>
          </div>

          <div className="flex justify-end">
            <AgentButton type="submit" disabled={isLoading}>
              {isLoading ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  <span>Gerando Plano de Aula...</span>
                </>
              ) : (
                <>
                  <PresentationChartBarIcon className="w-5 h-5" />
                  <span>Gerar Plano de Aula</span>
                </>
              )}
            </AgentButton>
          </div>
        </form>

        {error && <AgentError message={error} onRetry={canRetry ? onRetry : undefined} />}
      </AgentFormSection>

      {hasStarted && (
        <section className="mt-8">
          <div className="bg-card border border-border rounded-lg p-6">
            {isLoading && (
              <div className="flex flex-col items-center justify-center gap-4 py-8">
                <div className="flex items-center gap-3">
                  <span className="text-2xl">😇</span>
                  <h3 className="text-lg font-semibold text-foreground">
                    Enquanto trabalhamos, eleve seu pensamento e descanse na presença de Deus.
                  </h3>
                </div>
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-secondary"></div>
                {steps.length > 0 && steps[0]?.title && (
                  <p className="text-sm text-muted-foreground">{steps[0].title}</p>
                )}
                {elapsedTime > 0 && (
                  <p className="text-xs text-muted-foreground">
                    Tempo de execução: {formatElapsedTime(elapsedTime)}
                  </p>
                )}
              </div>
            )}
            
            {result && result.trim().length > 0 && (
              <>
                <div className="mt-6">
                  <AgentResult title="Aula Preparada">
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>
                      {result}
                    </ReactMarkdown>
                  </AgentResult>
                </div>
                <AgentDisclaimer />
                <div className="mt-6 flex items-center justify-end gap-3">
                  <AgentOutputActions
                    content={result}
                    agentName="Preparar Aula"
                    agentCode="preparar-aula"
                    reference={biblicalReference || 'Plano de Aula'}
                  />
                  <AgentButton
                    type="button"
                    onClick={handleReset}
                    variant="secondary"
                    aria-label="Reiniciar"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="w-5 h-5" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182m0-4.991v4.99" />
                    </svg>
                    <span>Reiniciar</span>
                  </AgentButton>
                </div>
              </>
            )}
          </div>
        </section>
      )}

      <SecurityViolationModal
        open={showSecurityModal}
        onClose={() => setShowSecurityModal(false)}
        onRestart={handleReset}
      />
      <CreditUpgradeNudgeModal
        isOpen={creditUpgradeNudgeOpen}
        onClose={dismissCreditUpgradeNudge}
        usagePercent={creditUpgradeNudgeUsagePercent}
      />
      {creditsExhausted && (
        <CreditExhaustedModal
          isOpen={creditsExhausted}
          onClose={() => setCreditsExhausted(false)}
          renewalDate={renewalDate}
          creditsInfo={creditsInfo || undefined}
        />
      )}
    </AgentPageLayout>
  );
};

export default LessonPreparer;
